import { Box, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { LocationModel } from "object-models/location-model";
import { useEffect, useMemo, useState } from "react";
import { DataItem, scopesCoveredOptions } from "../Data/PortfolioViewData";
import type { AssessmentViewMode } from "../Data/PortfolioViewData";
import { buildEntityEmissions } from "../Data/dataUtils";

interface EmissionByScopePieChatProps {
  data: LocationModel;
  selectedScope: number[];
  assessmentView: AssessmentViewMode;
  rawRows: Array<Partial<LocationModel>>;
  organizationNameById?: Record<number, string>;
  isOrganizationPortfolio?: boolean;
  displayContext?: "portfolio" | "location";
}

/**
 * A React functional component that renders a pie chart showing emissions by scope.
 * The chart dynamically updates based on the selected scopes and the emission data passed.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @param {LocationModel} props.data - The location emission data containing scope emission values.
 * @param {number[]} props.selectedScope - The list of selected scope IDs to filter the pie chart.
 */
const EmissionByScopePieChat: React.FC<EmissionByScopePieChatProps> = ({
  data,
  selectedScope,
  assessmentView,
  rawRows,
  organizationNameById = {},
  isOrganizationPortfolio = false,
  displayContext = "portfolio",
}) => {
  const [pieChartData, setPieChartData] = useState([] as Array<DataItem>);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(
    null,
  );
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const colorMap: Record<string, string> = {
    "Scope 1": "#ed9808",
    "Scope 2": "#c43b2e",
    "Scope 3": "#23b3a7",
  };

  const orgColorPalette = [
    "#4a86c8",
    "#e07c38",
    "#3d8b57",
    "#9b59b6",
    "#e74c3c",
    "#2980b9",
    "#16a085",
    "#f39c12",
  ];

  const getItemColor = (name: string, index: number): string => {
    if (assessmentView === "organization") {
      return orgColorPalette[index % orgColorPalette.length];
    }
    return colorMap[name] ?? "#8aa3b7";
  };

  /**
   * Generates and returns the pie chart data based on the emission data.
   *
   * @function
   * @returns {Array<DataItem>} - An array of DataItem objects containing the name and value of each scope's emission.
   */
  const pieData = (): Array<DataItem> => {
    const ndata = [
      {
        name: "Scope 1",
        value: data.scope1GHGEmission ?? 0,
      },
      {
        name: "Scope 2",
        value: data.scope2GHGEmission ?? 0,
      },
      {
        name: "Scope 3",
        value:
          data.scope3GHGEmission != null ? Number(data.scope3GHGEmission) : 0,
      },
    ];

    return ndata;
  };

  const organizationPieData = (): Array<DataItem> =>
    buildEntityEmissions(rawRows, selectedScope, isOrganizationPortfolio, organizationNameById)
      .map((item) => ({ name: item.name, value: item.total }));

  /**
   * Reshapes the pie chart data by calling `pieData` and updating the state.
   *
   * @function
   */
  function reshapeData() {
    setPieChartData(pieData);
  }

  // Update pie chart data when `data` changes
  useEffect(() => {
    if (assessmentView === "organization") {
      setPieChartData(organizationPieData());
      return;
    }

    reshapeData();
  }, [assessmentView, data, rawRows, selectedScope, isOrganizationPortfolio]);

  // Filter and update the pie chart data based on the selected scopes
  useEffect(() => {
    if (assessmentView === "organization") {
      return;
    }

    const scopeFilter = scopesCoveredOptions
      .filter((s) => selectedScope.includes(s.id))
      .map((s) => s.value);
    const newPieData = pieData().filter((d) => scopeFilter.includes(d.name));
    if (newPieData.length > 0) {
      setPieChartData(newPieData);
    } else {
      setPieChartData(pieData());
    }
  }, [assessmentView, selectedScope]);

  const totalEmissions = pieChartData.reduce(
    (sum, item) => sum + (Number(item.value) || 0),
    0,
  );

  const donutGradient = (() => {
    if (totalEmissions <= 0) {
      return "conic-gradient(#e8eef3 0deg 360deg)";
    }

    let cumulativeDegrees = 0;
    const segments = pieChartData
      .filter((item) => Number(item.value) > 0)
      .map((item, index) => {
        const segmentDegrees = (Number(item.value) / totalEmissions) * 360;
        const start = cumulativeDegrees;
        cumulativeDegrees += segmentDegrees;
        const end = cumulativeDegrees;
        return `${getItemColor(item.name, index)} ${start}deg ${end}deg`;
      });

    return `conic-gradient(${segments.join(",")})`;
  })();
  const segmentAngles = useMemo(() => {
    if (totalEmissions <= 0) return [];
    let cum = 0;
    return pieChartData
      .filter((item) => Number(item.value) > 0)
      .map((item, index) => {
        const deg = (Number(item.value) / totalEmissions) * 360;
        const start = cum;
        cum += deg;
        return { start, end: cum, index, item };
      });
  }, [pieChartData, totalEmissions]);

  const handleDonutMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const outerRadius = rect.width / 2;
    const innerRadius = outerRadius * 0.53;
    if (dist < innerRadius || dist > outerRadius) {
      setHoveredSegmentIndex(null);
      setTooltipPos(null);
      return;
    }
    const angleFromRight = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
    const conicAngle = (angleFromRight - 180 + 360) % 360;
    const seg =
      segmentAngles.find((s) => conicAngle >= s.start && conicAngle < s.end) ??
      (conicAngle >= (segmentAngles[segmentAngles.length - 1]?.start ?? 0)
        ? segmentAngles[segmentAngles.length - 1]
        : undefined);
    setHoveredSegmentIndex(seg !== undefined ? seg.index : null);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleDonutMouseLeave = () => {
    setHoveredSegmentIndex(null);
    setTooltipPos(null);
  };

  let pieChartTitle: string;
  if (assessmentView === "organization") {
    pieChartTitle = isOrganizationPortfolio
      ? "Total Emissions by Location"
      : "Total Emissions by Organization";
  } else {
    pieChartTitle = "Emissions by Scope";
  }

  const isLocationScopeView =
    displayContext === "location" && assessmentView !== "organization";
  const layoutStackSpacing = isLocationScopeView ? 1.5 : 2.5;
  const layoutStackPadding = isLocationScopeView
    ? { xs: 0.75, md: 1.25 }
    : { xs: 1, md: 2 };
  const donutSize = isLocationScopeView
    ? { xs: 180, md: 220 }
    : { xs: 220, md: 260 };
  const donutInnerSize = isLocationScopeView
    ? { xs: 96, md: 116 }
    : { xs: 118, md: 138 };

  return (
    <>
      <Grid
        item
        xs={12}
        md={5.5}
        sx={{
          width: "100%",
          maxWidth: "100%",
          borderRight: "1px solid #e4eaf0",
        }}
      >
        <Stack spacing={layoutStackSpacing} sx={{ p: layoutStackPadding }}>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "#3a4d67",
              fontWeight: 450,
              fontSize: isLocationScopeView ? "0.68rem" : "0.80rem",
              whiteSpace: "nowrap",
            }}
          >
            {pieChartTitle}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              data-testid="emission-donut"
              onMouseMove={handleDonutMouseMove}
              onMouseLeave={handleDonutMouseLeave}
              sx={{
                width: donutSize,
                height: donutSize,
                borderRadius: "50%",
                background: donutGradient,
                transform: "rotate(-90deg)",
                position: "relative",
                border: "5px solid #fff",
                boxShadow: "0 0 0 1px #edf1f5",
                cursor: hoveredSegmentIndex !== null ? "pointer" : "default",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: donutInnerSize,
                  height: donutInnerSize,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "inset 0 0 0 1px #edf1f5",
                }}
              />
            </Box>
          </Box>

          <Stack spacing={isLocationScopeView ? 0.85 : 1.15}>
            {pieChartData
              .filter((item) => Number(item.value) > 0)
              .map((item, index) => {
                const value = Number(item.value) || 0;
                const percentage =
                  totalEmissions > 0 ? (value / totalEmissions) * 100 : 0;
                const itemColor = getItemColor(item.name, index);
                const tooltipContent =
                  assessmentView === "organization" ? (
                    <Box sx={{ p: 0.5 }}>
                      <Typography
                        sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.25 }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.82rem" }}>
                        {value.toLocaleString("en-IN")} tCO₂e (
                        {percentage.toFixed(1)}%)
                      </Typography>
                    </Box>
                  ) : (
                    `${value.toLocaleString("en-IN")} tCO₂e`
                  );

                return (
                  <Box key={item.name}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          assessmentView === "organization"
                            ? "minmax(110px, 1.5fr) 1fr auto auto"
                            : "94px 1fr auto auto",
                        alignItems: "center",
                        columnGap: 1.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: itemColor,
                          }}
                        />
                        <Tooltip title={item.name} placement="top" arrow>
                          <Typography
                            sx={{
                              color:
                                assessmentView === "organization"
                                  ? itemColor
                                  : "#34495e",
                              fontWeight:
                                assessmentView === "organization" ? 500 : 400,
                              fontSize: isLocationScopeView ? "0.70rem" : "0.82rem",
                              maxWidth:
                                assessmentView === "organization"
                                  ? 220
                                  : "none",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Tooltip>
                      </Box>

                      <Box
                        sx={{
                          height: 10,
                          borderRadius: 10,
                          backgroundColor: "#e9edf2",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.max(0, Math.min(100, percentage))}%`,
                            height: "100%",
                            borderRadius: 10,
                            backgroundColor: itemColor,
                          }}
                        />
                      </Box>

                      <Tooltip title={tooltipContent} placement="top" arrow>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#0f2b52",
                            minWidth: 90,
                            fontSize: isLocationScopeView ? "0.70rem" : "0.9rem",
                          }}
                        >
                          {value.toLocaleString("en-IN")}
                        </Typography>
                      </Tooltip>
                      <Tooltip title={tooltipContent} placement="top" arrow>
                        <Typography
                          sx={{
                            color: "#8091a8",
                            minWidth: 42,
                            fontSize: isLocationScopeView ? "0.70rem" : "0.86rem",
                          }}
                        >
                          {`${percentage.toFixed(1)}%`}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}

            <Box
              sx={{
                mt: 0.4,
                pt: 1,
                borderTop: "1px solid #edf1f5",
                display: "grid",
                gridTemplateColumns:
                  assessmentView === "organization"
                    ? "minmax(110px, 1.5fr) 1fr auto auto"
                    : "94px 1fr auto auto",
                columnGap: 1.5,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#5c6f84",
                  fontSize: isLocationScopeView ? "0.82rem" : "0.9rem",
                  pl: 3.5,
                }}
              >
                Total
              </Typography>
              <Box />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0f2b52",
                  minWidth: 90,
                  fontSize: isLocationScopeView ? "0.82rem" : "0.9rem",
                }}
              >
                {totalEmissions.toLocaleString("en-IN")}
              </Typography>
              <Typography
                sx={{
                  color: "#8091a8",
                  minWidth: 42,
                  fontSize: isLocationScopeView ? "0.8rem" : "0.86rem",
                }}
              >
                100%
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Grid>
      {hoveredSegmentIndex !== null &&
        tooltipPos !== null &&
        (() => {
          const seg = segmentAngles.find(
            (s) => s.index === hoveredSegmentIndex,
          );
          if (!seg) return null;
          const value = Number(seg.item.value) || 0;
          const percentage =
            totalEmissions > 0 ? (value / totalEmissions) * 100 : 0;
          return (
            <Box
              data-testid="emission-fixed-tooltip"
              sx={{
                position: "fixed",
                left: tooltipPos.x + 14,
                top: tooltipPos.y - 14,
                bgcolor: "rgba(15, 43, 82, 0.92)",
                color: "#fff",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                pointerEvents: "none",
                zIndex: 9999,
                boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
              }}
            >
              {assessmentView === "organization" ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      mb: 0.25,
                      color: "#fff",
                    }}
                  >
                    {seg.item.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.79rem", color: "#ccd8e8" }}>
                    {value.toLocaleString("en-IN")} tCO₂e (
                    {percentage.toFixed(1)}%)
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: "0.82rem", color: "#fff" }}>
                  {value.toLocaleString("en-IN")} tCO₂e
                </Typography>
              )}
            </Box>
          );
        })()}
    </>
  );
};
export default EmissionByScopePieChat;
